import { FC } from 'react';
import useToggle from '@stripcash/ui-kit/utils/hooks/useToggle';
import cn from 'classnames';
import { Transition } from 'motion';
import { AnimatePresence, motion } from 'motion/react';
import Button, { ButtonColor } from '@stripcash/ui-kit/components/Button';
import ArrowTipUpIcon from '@stripcash/ui-kit/icons/arrowTipUp.svg';
import Text, { TextPresets } from '@stripcash/ui-kit/components/Text';

import { useT } from 'common/i18n';
import { Survey, SurveyDataProps } from 'common/apps/modules/survey/types';
import { GlobalStateConsumer } from 'common/apps/core/StateConsumer';
import rootSurveySelector from 'common/apps/modules/survey/selectors/rootSurveySelector';

import s from './styles.module.css';
import SmilesGroupSwitch from './components/SmilesGroupSwitch';
import CallSurveyLine from './components/CallSurveyLine';

type SurveyQuestionsProps = {
  onShowModal: Survey.ModuleApi['handleShowModal'];
};

const SlideTransition: Transition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.3,
};

const SurveyQuestions: FC<SurveyQuestionsProps> = ({ onShowModal }) => {
  const [isSurveyDropdownOpen, toggleSurveyDropdown] = useToggle(true);
  const t = useT('questionnaire');

  return (
    <GlobalStateConsumer selector={rootSurveySelector}>
      {({ surveyData, isSurveySubmitted }) => (
        <>
          {!isSurveySubmitted && surveyData !== null && (
            <AnimatePresence>
              <motion.div
                animate={{ y: isSurveyDropdownOpen ? '0%' : '-100%' }}
                transition={SlideTransition}
                className={cn(s.surveyDropdown, {
                  [s.surveyDropdownOpen]: isSurveyDropdownOpen,
                })}
              >
                <div className={s.container}>
                  <div className={s.containerContent}>
                    <div className={s.containerContentLeft}>
                      <div className={s.containerContentLeftSmiles}>
                        <SmilesGroupSwitch />
                      </div>

                      <div className={s.containerContentLeftText}>
                        <Text tagName="div" preset={TextPresets.BodyBold14}>
                          {t((surveyData as SurveyDataProps).title)}
                        </Text>
                        <Text tagName="div">
                          {t((surveyData as SurveyDataProps).description)}
                        </Text>
                      </div>
                    </div>

                    <Button color={ButtonColor.Default} onClick={onShowModal}>
                      {t('callDropdown.submit')}
                    </Button>
                  </div>

                  <button
                    type="button"
                    className={s.hideButton}
                    onClick={toggleSurveyDropdown}
                  >
                    <ArrowTipUpIcon size={12} color="white" />
                  </button>
                </div>

                <CallSurveyLine
                  onClick={toggleSurveyDropdown}
                  isOpen={isSurveyDropdownOpen}
                />
              </motion.div>
            </AnimatePresence>
          )}
        </>
      )}
    </GlobalStateConsumer>
  );
};

export default SurveyQuestions;
